import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Divider, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchField from 'src/shared/components/form/searchField';
import { GroupMessageEmailGrid } from 'src/modules/groupMessageEmail/components/groupMessageEmailGrid';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { GroupMessageEmailSearchFilter } from '../models/groupMessageEmailSearchFilter';
import { GroupMessageEmailTabs } from '../components/groupMessageEmailTabs';

export const GroupMessageEmailOverviewPage = () => {
  const isMounted = useIsMounted();
  const { t } = useTranslation(['groupMessage']);
  const { currentUser } = useGlobals();
  // Användaren måste tillhöra fler än en förening för att checkboxen ska vara relevant — annars är allt redan filtrerat.
  const canDistribute = (currentUser?.associations?.length ?? 0) > 1;
  const [, setLoading] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [groupMessageEmailSearchFilter, setGroupMessageEmailSearchFilter] = useLocalStorage(
    LocalStorageKeys.GroupMessageEmailSearchFilter,
    {} as unknown as GroupMessageEmailSearchFilter,
  );

  const searchFilter: GroupMessageEmailSearchFilter = useMemo(() => {
    return {
      searchString: groupMessageEmailSearchFilter?.searchString,
      isSent: groupMessageEmailSearchFilter?.isSent,
      activeTab: groupMessageEmailSearchFilter?.activeTab,
      archivedMessages: groupMessageEmailSearchFilter?.archivedMessages,
      onlyFederation: canDistribute ? groupMessageEmailSearchFilter?.onlyFederation : false,
    } as unknown as GroupMessageEmailSearchFilter;
  }, [
    canDistribute,
    groupMessageEmailSearchFilter?.activeTab,
    groupMessageEmailSearchFilter?.archivedMessages,
    groupMessageEmailSearchFilter?.isSent,
    groupMessageEmailSearchFilter?.onlyFederation,
    groupMessageEmailSearchFilter?.searchString,
  ]);

  const loadingCallback = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setGroupMessageEmailSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setGroupMessageEmailSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setGroupMessageEmailSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setGroupMessageEmailSearchFilter, tempSearchString]);

  return (
    <>
      <PageFilterSection
        leftContent={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <SearchField
              initialValue={searchFilter.searchString || ''}
              onSearch={(newSearchString) => {
                setSearchInitialized(true);
                setTempSearchString(newSearchString);
              }}
            />
            {canDistribute && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!searchFilter.onlyFederation}
                    size='small'
                    onChange={(e) => setGroupMessageEmailSearchFilter((prev) => ({ ...prev, onlyFederation: e.target.checked }))}
                  />
                }
                label={t('groupMessage:overview.showOnlyFederation')}
              />
            )}
          </Box>
        }
      />
      <GroupMessageEmailTabs
        initialValue={searchFilter.activeTab}
        callback={(newActiveTab, newIsSent, archivedMessages) => {
          setGroupMessageEmailSearchFilter((prev) => ({
            ...prev,
            ...{ activeTab: newActiveTab, isSent: newIsSent, archivedMessages },
          }));
        }}
      />
      <Divider />
      <GroupMessageEmailGrid loadingCallback={loadingCallback} searchFilter={searchFilter} />
    </>
  );
};
